import React from 'react'

import { collectHeadings } from './collectHeadings'
import { articleHeadingsKey } from './constants'
import type { ArticleHeading } from './types'

const EMPTY_HEADINGS: ArticleHeading[] = []

// Returns the article's headings, either from `override` or by scanning the
// rendered content.
//
// The scan runs after mount, never during render, so the server and the first
// client render agree (both empty) and hydration matches. Callers that know
// their headings server-side pass `override` and get a table of contents in the
// server HTML instead.
export function useArticleHeadings(
  contentRef: React.RefObject<HTMLElement | null>,
  override?: ArticleHeading[]
): ArticleHeading[] {
  const hasOverride = override != null && override.length > 0
  const [scanned, setScanned] = React.useState<ArticleHeading[]>(EMPTY_HEADINGS)
  const scannedKeyRef = React.useRef('')

  React.useEffect(() => {
    const content = contentRef.current
    if (hasOverride || !content) {
      return
    }

    const rescan = () => {
      const next = collectHeadings(content)
      const nextKey = articleHeadingsKey(next)
      // Without this guard the state update re-renders the children, which the
      // observer sees as a mutation, which scans again — a render loop. Keeping
      // the previous array on an unchanged key also keeps the reference stable
      // for useActiveHeadingId's dependency.
      if (nextKey === scannedKeyRef.current) {
        return
      }
      scannedKeyRef.current = nextKey
      setScanned(next)
    }

    rescan()

    let frame = 0
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(rescan)
    })
    observer.observe(content, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
    // `children` is deliberately not a dependency: it is a new element object on
    // every parent render, which would rebuild the observer constantly. The
    // MutationObserver is what picks content changes up.
  }, [contentRef, hasOverride])

  return hasOverride ? override : scanned
}
